import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionTag } from "./Shared";

const services = [
  {
    n: "01",
    title: "Property Advisory",
    sub: "Yield & Compliance Blueprint",
    text: "A fixed-fee written engagement that stress-tests your asset's rental positioning, redesigns layouts for premium let-ability, and audits your regulatory exposure.",
    bullets: ["Micro-market yield modelling per asset", "Layout & let-ability redesign", "Licensing, tax & tenancy compliance audit"],
    fee: "Fixed engagement · $2.5k – $5k / asset",
  },
  {
    n: "02",
    title: "Refurbishment & Vendor Oversight",
    sub: "Sourcing & Site Audits",
    text: "Independent supervision of refurbishments and capital works. We source, vet, and police contractors — funds never leave your custody.",
    bullets: ["Contractor sourcing & vetting", "Milestone, quality & material audits", "Invoice verification & dispute mediation"],
    fee: "Performance-linked · 12% of contract value",
  },
  {
    n: "03",
    title: "Operator Selection & Stewardship",
    sub: "Retained Owner-Side Advisor",
    text: "We run the operator search, benchmark the candidates, and audit their performance monthly — you choose the operator; we hold them to standard.",
    bullets: ["Operator RFP & fee benchmarking", "SLA design & enforcement", "Monthly remote performance audit"],
    fee: "Retainer · $1,500 / month / unit",
  },
];

export default function Services() {
  return (
    <section id="practice" data-testid="services-section" className="border-b border-black/15">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 py-24 md:py-40">
        <div className="grid lg:grid-cols-12 gap-10 mb-20 md:mb-28">
          <div className="lg:col-span-4">
            <SectionTag testId="services-tag">The advisory</SectionTag>
          </div>
          <Reveal delay={0.1} className="lg:col-span-8">
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
              Three lines of <em className="font-light">owner-side counsel.</em>
            </h2>
            <p className="mt-8 max-w-xl text-base lg:text-lg leading-relaxed text-black/60 font-light">
              Engage a single line, or compose all three into one written mandate — fixed scope, aligned
              incentives, and custody of funds with you throughout.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 border-t border-l border-black/15">
          {services.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12} className="h-full">
              <article
                data-testid={`service-card-${s.n}`}
                className="group relative h-full flex flex-col p-8 md:p-12 border-b border-r border-black/15 transition-colors duration-500 hover:bg-[#EBE9E1]"
              >
                <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.25em] text-black/45">
                  <span>Service · {s.n}</span>
                  <ArrowUpRight
                    size={18}
                    strokeWidth={1.5}
                    className="text-[#D4512D] opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-500"
                  />
                </div>
                <h3 className="mt-10 font-serif text-3xl md:text-4xl tracking-tight">{s.title}</h3>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4512D]">{s.sub}</p>
                <p className="mt-6 text-sm leading-relaxed text-black/55 font-light">{s.text}</p>
                <ul className="mt-8 space-y-3 flex-1">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-sm text-black/70 font-light">
                      <span className="mt-[7px] inline-block w-1.5 h-1.5 bg-[#D4512D] shrink-0" aria-hidden="true" />
                      {b}
                    </li>
                  ))}
                </ul>
                <p className="mt-10 pt-6 border-t border-black/15 font-mono text-[11px] uppercase tracking-[0.2em] text-black/60">
                  {s.fee}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
